import { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';

export function PWAInstallPrompt() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showBanner, setShowBanner] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [showIOSGuide, setShowIOSGuide] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);

    useEffect(() => {
        // Check if already installed
        if (window.matchMedia('(display-mode: standalone)').matches) {
            setIsInstalled(true);
            return;
        }

        // Check if dismissed recently
        const dismissed = localStorage.getItem('pwa-dismissed');
        if (dismissed) {
            const dismissedTime = parseInt(dismissed);
            if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) return; // 7 days
        }

        // Detect iOS
        const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        setIsIOS(isIOSDevice);

        // Listen for beforeinstallprompt (Chrome/Edge/Samsung)
        const handleBeforeInstall = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            // Show banner after 30 seconds of browsing
            setTimeout(() => setShowBanner(true), 30000);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstall);

        // For iOS, show banner after delay
        if (isIOSDevice) {
            setTimeout(() => setShowBanner(true), 45000);
        }

        // Listen for app installed
        window.addEventListener('appinstalled', () => {
            setIsInstalled(true);
            setShowBanner(false);
            setDeferredPrompt(null);
        });

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
        };
    }, []);

    const handleInstall = async () => {
        if (isIOS) {
            setShowIOSGuide(true);
            return;
        }

        if (!deferredPrompt) return;

        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;

        if (outcome === 'accepted') {
            setShowBanner(false);
        }
        setDeferredPrompt(null);
    };

    const handleDismiss = () => {
        setShowBanner(false);
        setShowIOSGuide(false);
        localStorage.setItem('pwa-dismissed', Date.now().toString());
    };

    if (isInstalled || !showBanner) return null;

    // iOS Guide Modal
    if (showIOSGuide) {
        return (
            <div className="fixed inset-0 z-[100] bg-black/50 flex items-end sm:items-center justify-center p-4" data-testid="pwa-ios-guide">
                <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-sm p-6 animate-fade-in-up">
                    <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-2">
                            <Smartphone className="w-6 h-6 text-primary" />
                            <h3 className="font-bold text-lg">Zainstaluj Klasowisko</h3>
                        </div>
                        <button onClick={handleDismiss} className="text-gray-400 hover:text-gray-600">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="space-y-4 text-sm text-gray-700">
                        <div className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs">1</span>
                            <p>Kliknij przycisk <strong>Udostepnij</strong> <span className="inline-block w-5 h-5 align-middle">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-primary">
                                    <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"/>
                                </svg>
                            </span> na dole ekranu</p>
                        </div>
                        <div className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs">2</span>
                            <p>Przewin w dol i wybierz <strong>"Dodaj do ekranu poczatkowego"</strong></p>
                        </div>
                        <div className="flex items-start gap-3">
                            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs">3</span>
                            <p>Kliknij <strong>"Dodaj"</strong> - gotowe!</p>
                        </div>
                    </div>
                    <button 
                        onClick={handleDismiss}
                        className="w-full mt-5 py-2.5 rounded-lg bg-primary text-white font-semibold hover:bg-primary/90 transition-colors"
                    >
                        Rozumiem!
                    </button>
                </div>
            </div>
        );
    }

    // Install Banner
    return (
        <div 
            className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-[380px] z-[90] animate-fade-in-up"
            data-testid="pwa-install-banner"
        >
            <div className="bg-white rounded-xl shadow-xl border border-border overflow-hidden">
                {/* Banner header */}
                <div className="bg-gradient-to-r from-[#0066CC] to-[#004499] px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <img src="/icons/icon-72x72.png" alt="" className="w-8 h-8 rounded-lg" />
                        <div>
                            <h4 className="text-white font-bold text-sm">Klasowisko.pl</h4>
                            <p className="text-blue-200 text-xs">Zainstaluj aplikacje</p>
                        </div>
                    </div>
                    <button onClick={handleDismiss} className="text-white/60 hover:text-white">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                
                {/* Banner content */}
                <div className="p-4">
                    <p className="text-sm text-gray-600 mb-3">
                        Zainstaluj Klasowisko na swoim telefonie! Szybki dostep do znajomych, wiadomosci i szkol.
                    </p>
                    <div className="flex gap-2">
                        <button
                            onClick={handleInstall}
                            className="flex-1 py-2.5 px-4 rounded-lg bg-[#0066CC] hover:bg-[#0055AA] text-white font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
                            data-testid="pwa-install-btn"
                        >
                            <Download className="w-4 h-4" />
                            Zainstaluj
                        </button>
                        <button
                            onClick={handleDismiss}
                            className="py-2.5 px-4 rounded-lg border border-gray-200 text-gray-500 text-sm hover:bg-gray-50 transition-colors"
                        >
                            Nie teraz
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
